import LegalLayout from "@/pages/legal/LegalLayout";
import { LEGAL } from "@/lib/legal";

/** Section wrapper — keeps typography consistent across all legal pages. */
const S = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-20">
    <h2 className="font-heading text-2xl font-bold tracking-tight text-white mt-10 mb-3">
      {title}
    </h2>
    <div className="space-y-4 text-zinc-300 text-[15px] leading-relaxed">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      testid="privacy-policy"
      title="Privacy Policy"
      subtitle={`How ${LEGAL.brand} (${LEGAL.company}) handles your personal data under the EU GDPR.`}
    >
      <p>
        This Privacy Policy explains what personal data we collect when you use{" "}
        <strong>{LEGAL.brand}</strong>, why we collect it, how long we keep it,
        and the rights you have over it. {LEGAL.company} is the data controller
        for {LEGAL.brand}.
      </p>

      <S id="data-we-collect" title="1. Data we collect">
        <p>We collect only the data we need to run the service:</p>
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-amber-400/60">
          <li><strong>Account data:</strong> email address, display name, hashed password, subscription tier, credit balance.</li>
          <li><strong>Content you upload:</strong> photos, prompts, project settings, generated videos.</li>
          <li><strong>Usage data:</strong> the projects you create, the AI-quality tier chosen, credits consumed, and generation success / failure events used to keep the platform reliable.</li>
          <li><strong>Billing data:</strong> when you buy credits or a subscription, our payment processor (Stripe) collects your name, billing address and card details. We only receive the last 4 digits and a Stripe customer ID — we never see your full card number.</li>
          <li><strong>Diagnostic data:</strong> browser, device type, operating system and error codes if you file a problem report.</li>
          <li><strong>Cookies:</strong> see our <a href="/legal/cookies" className="text-amber-400 hover:underline">Cookie Policy</a>.</li>
        </ul>
      </S>

      <S id="why" title="2. Why we process your data">
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-amber-400/60">
          <li>To create and secure your account (legal basis: contract).</li>
          <li>To generate videos from the photos you upload (legal basis: contract).</li>
          <li>To bill you and prevent fraud (legal basis: contract & legitimate interest).</li>
          <li>To keep the platform reliable and improve routing between AI engines (legal basis: legitimate interest).</li>
          <li>To send transactional emails such as confirmations, invoices and important service notices (legal basis: contract).</li>
        </ul>
      </S>

      <S id="who" title="3. Who processes your data">
        <p>We rely on the following sub-processors. Each has its own privacy notice and appropriate GDPR safeguards:</p>
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-amber-400/60">
          <li><strong>Hosting &amp; database</strong> — for running the application and storing your account and content.</li>
          <li><strong>AI video generation engines</strong> — your photos are transmitted to a third-party image-to-video engine only for the duration of the render. We do not grant the engine any right to reuse your content for training.</li>
          <li><strong>Payments</strong> — Stripe processes card data and stores billing details on our behalf.</li>
          <li><strong>Transactional email delivery</strong> — used to send you sign-up, invitation and receipt emails.</li>
        </ul>
        <p>We never sell or rent your personal data. We never share it with third parties for advertising.</p>
      </S>

      <S id="retention" title="4. How long we keep your data">
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-amber-400/60">
          <li><strong>Account &amp; content:</strong> kept while your account is active. You can delete projects or your entire account at any time — deletion is permanent.</li>
          <li><strong>Billing records &amp; invoices:</strong> retained for 7 years to comply with Dutch tax law.</li>
          <li><strong>Reliability &amp; error logs:</strong> retained for up to 90 days, then purged.</li>
          <li><strong>Email delivery events:</strong> retained for up to 90 days.</li>
        </ul>
      </S>

      <S id="rights" title="5. Your GDPR rights">
        <p>Because we operate from {LEGAL.country} and offer the service across the EU, you have the following rights over your personal data:</p>
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-amber-400/60">
          <li><strong>Access</strong> — request a copy of the data we hold about you.</li>
          <li><strong>Rectification</strong> — correct anything that is wrong.</li>
          <li><strong>Erasure</strong> — request full deletion of your account and content.</li>
          <li><strong>Portability</strong> — receive your data in a machine-readable format.</li>
          <li><strong>Restriction &amp; objection</strong> — limit how we process your data.</li>
          <li><strong>Complaint</strong> — lodge a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens) if you feel we mishandled your data.</li>
        </ul>
        <p>To exercise any right, email{" "}
          <a className="text-amber-400 hover:underline" href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a>.
          We will respond within one calendar month.
        </p>
      </S>

      <S id="security" title="6. Security">
        <p>
          We follow industry-standard practices: passwords are hashed with bcrypt, transport is
          encrypted end-to-end using HTTPS, and access to production data is restricted to
          authorised operators. No system is 100% impregnable — if you notice anything
          suspicious about your account please contact us immediately.
        </p>
      </S>

      <S id="minors" title="7. Age restriction">
        <p>{LEGAL.brand} is not directed to anyone under 16. If you believe we have collected data from a minor, please contact us and we will delete it.</p>
      </S>

      <S id="changes" title="8. Changes to this policy">
        <p>
          We may update this policy from time to time. When changes are material we will
          notify you by email or an in-app banner before the change takes effect.
        </p>
      </S>
    </LegalLayout>
  );
}
